/**
 * Decides whether the "Updates from GoodSender" onboarding step applies to a
 * given workspace. It applies only to the account's REGISTRATION workspace —
 * the first entry of the caller's `getWorkspaces` list, in creation order
 * (`getWorkspacesFromDB` orders `createdAt asc, workspaceId asc`, D-1014-1;
 * `createUser` creates only that first workspace, every later one comes from
 * `createWorkspace`, and no delete/invite flow can reorder it). The offer was
 * made once, at registration — see spec 1136 §Clarifications: an additional
 * workspace always skips the step, regardless of the account's consent state.
 *
 * Import-free leaf, like `blog/senderIdentity.ts` — callers pass the list
 * they already have rather than this module re-fetching it. `workspaces`
 * MUST be given in the order the server returned it; this never re-sorts.
 *
 * Fails OPEN: when the list is missing or empty (unknown), the step is
 * shown — today's behaviour — never silently skipped (spec 1136 A3, S4).
 */
export function showsUpdatesStep(
  workspaces: ReadonlyArray<{ workspaceId: string }> | undefined,
  workspaceId: string,
): boolean {
  const first = workspaces?.[0];
  if (!first) return true;
  return first.workspaceId === workspaceId;
}

type OnboardingStepAfterDomain =
  | { to: "/onboarding/$workspaceId/updates"; params: { workspaceId: string } }
  | { to: "/onboarding/$workspaceId/first-post"; params: { workspaceId: string } };

/** Both address exits visit the optional designer before the legacy branch. */
export function onboardingDesignStep(workspaceId: string) {
  return {
    to: "/onboarding/$workspaceId/design" as const,
    params: { workspaceId },
  };
}

/**
 * The single "what's next after the designer step" decision —
 * `showsUpdatesStep` plus the two destinations either answer selects. Every
 * caller that exits the designer or a legacy Cloudflare callback already in
 * flight MUST go through this rather than re-deriving the same branch, so a non-
 * registration workspace lands on `/first-post` directly instead of bouncing
 * off `/updates`'s own `guardUpdatesStep` redirect.
 */
export function nextOnboardingStepAfterDomain(
  workspaces: ReadonlyArray<{ workspaceId: string }> | undefined,
  workspaceId: string,
): OnboardingStepAfterDomain {
  return showsUpdatesStep(workspaces, workspaceId)
    ? { to: "/onboarding/$workspaceId/updates", params: { workspaceId } }
    : { to: "/onboarding/$workspaceId/first-post", params: { workspaceId } };
}
